/* Supporting Libraries */
import type { ComponentType, ReactNode, JSX } from 'react';
import { Navigate, useParams, Params } from 'react-router-dom'; // eslint-disable-line @typescript-eslint/consistent-type-imports
import type { IFlagsmithFeature } from 'flagsmith/types';
import { isString } from 'lodash';
import { isNotNil } from '~/utilities/type-guards';
import type { RedirectUnlessAllowedFeatureFlagsProps } from '../components/RedirectUnless';
import {
  RedirectUnlessAllowedParams,
  RedirectUnlessFeatureFlagEnabled,
} from '../components/RedirectUnless';
import type { SupportedFeatureFlags } from '../hooks/useLoadFeatureFlags';
import { useLoadFeatureFlags } from '../hooks/useLoadFeatureFlags';

export type RoutableComponent<Params> = ComponentType<Params> & {
  isRoutableComponent: true;
};

/**
 * This method should be used sparingly to mark a component safe for routing. We ideally
 * only want components that are lazy loaded or are composed of components which are lazy
 * loaded. Not doing this will cause bloat accross the application.
 */
export const dangerous_markComponentAsRoutable = <Params,>(
  component: ComponentType<Params>,
): RoutableComponent<Params> => {
  const newComponent = component as unknown as RoutableComponent<Params>;
  newComponent.isRoutableComponent = true;

  return newComponent;
};

type FlaggedRouteProps<Params extends object> = {
  flag: SupportedFeatureFlags;
  path?: string;
  checkValue?: (value: IFlagsmithFeature['value']) => boolean;
  props: Params;
  enabled: RoutableComponent<Params>;
  disabled: RoutableComponent<Params>;
};

const FlaggedRoute = <Params extends object>({
  flag,
  enabled,
  disabled,
  checkValue = () => true,
  props,
}: FlaggedRouteProps<Params>) => {
  const { flags } = useLoadFeatureFlags([flag]);
  const Component = flags[flag].enabled && checkValue(flags[flag].value) ? enabled : disabled;

  return <Component {...props} />;
};

FlaggedRoute.isRoutableComponent = true;

type RedesignRouteProps<Params extends object> = Omit<FlaggedRouteProps<Params>, 'flag'>;
const RedesignRouteBody = <Params extends object>(
  props: RedesignRouteProps<Params>,
): JSX.Element => {
  return (
    <FlaggedRoute
      {...props}
      flag="flag_app_redesign_2024"
      checkValue={(flagValue) => {
        if (isString(flagValue) && isString(props.path)) {
          const enabledPaths = JSON.parse(flagValue) as string[];
          return enabledPaths.includes(props.path);
        }

        return false;
      }}
    />
  );
};

export const RedesignRoute = dangerous_markComponentAsRoutable(RedesignRouteBody);

export class RouteDefinition<P extends object> {
  public readonly path: string;

  private readonly component: RoutableComponent<P>;

  private readonly props: P;

  public constructor({
    path,
    component,
    props,
  }: {
    path: string;
    component: RoutableComponent<P>;
    props: P;
  }) {
    this.path = path;
    this.component = component;
    this.props = props;
  }

  public render(): JSX.Element {
    const Component = this.component;
    return (
      <Component
        path={this.path}
        {...this.props}
      />
    );
  }
}

type NewURLFunction = (params: Readonly<Params>) => string;

export const redirector = <Params extends object>(
  newURLFunction: NewURLFunction,
): RoutableComponent<Params> => {
  const Component = (): JSX.Element => {
    const params = useParams();
    const newURL = newURLFunction(params);

    return <Navigate to={newURL} />;
  };

  return dangerous_markComponentAsRoutable(Component);
};

export const redirectUnlessAllowedParams = <Params extends object>({
  allowedParams,
  to,
  component,
}: {
  allowedParams: Record<string, string[]>;
  to: string;
  component: RoutableComponent<Params>;
}): RoutableComponent<Params> => {
  const ComponentToRender = component;
  const Component = (props: Params): JSX.Element => {
    return (
      <RedirectUnlessAllowedParams
        to={to}
        allowedParams={allowedParams}
      >
        <ComponentToRender {...props} />
      </RedirectUnlessAllowedParams>
    );
  };

  return dangerous_markComponentAsRoutable(Component);
};

type RedirectUnlessAllowedFeatureFlagsPropsWithRoutableChildren<Params extends object> = Omit<
  RedirectUnlessAllowedFeatureFlagsProps,
  'children'
> & {
  component: RoutableComponent<Params>;
};

export const redirectUnlessFeatureFlagEnabled = <Params extends object>({
  to,
  flag,
  component,
  hardRedirect,
  flagConditionToAllow,
}: RedirectUnlessAllowedFeatureFlagsPropsWithRoutableChildren<Params>): RoutableComponent<Params> => {
  const ComponentToRender = component;
  const Component = (props: Params): JSX.Element => {
    return (
      <RedirectUnlessFeatureFlagEnabled
        to={to}
        flag={flag}
        hardRedirect={hardRedirect}
        flagConditionToAllow={flagConditionToAllow}
      >
        <ComponentToRender {...props} />
      </RedirectUnlessFeatureFlagEnabled>
    );
  };

  return dangerous_markComponentAsRoutable(Component);
};

type Layout = RoutableComponent<{ children: ReactNode }>;

export type ScreenDefinition<Props> = {
  layout?: Layout;
  content: RoutableComponent<Props>;
};

export const routeViaScreenDefinition = <Props extends object>(
  screenDefinition: ScreenDefinition<Props>,
): RoutableComponent<Props> => {
  const Layout = screenDefinition.layout;
  const Content = screenDefinition.content;

  if (isNotNil(Layout)) {
    const LayoutComponent = (props: Props) => {
      return (
        <Layout>
          <Content {...props} />
        </Layout>
      );
    };

    return dangerous_markComponentAsRoutable(LayoutComponent);
  }

  return Content;
};
